import {
  ISLANDS,
  IS_MOBILE_REGEX,
  PRICES_API_SERVICE_TYPES,
  PRICES_API_VEHICLES,
  COUNTRIES,
  PRICES_API_ISLANDS,
  SERVICE_TYPES,
  ACTION_STATUS,
  PAYMENT_METHODS,
  IN_OUT,
  SERVICE_TYPES_NAMES,
  PRICES_TO_NINJA_VEHICLE_TYPES,
  EXCURSION_MODE,
  CP_PROVINCES,
  ISLANDS_NUMERIC_KEY,
  MIN_DAYS_TO_BOOK_SHUTTLE_OUT,
  MIN_DAYS_TO_BOOK,
  AIRPORTS_IDS
} from './constants'
import { t } from './translations'
import type {
  PaymentParams,
  NinjaAvailabilityCombo,
  NinjaSaveBooking,
  PricesAPIRequest,
  SelectObject,
  PurchaseInfo,
  PlacePosition,
  AvailableService,
  CustomPlace,
  AnyObject,
  ClientData,
  PricesAPIResponse,
  MeetingPointsAuxObj,
  HomeSelectedInfo
} from './types'
import { redirect } from '@sveltejs/kit'
import { ninjaFetch } from './fetch'
import { env } from '$env/dynamic/public'
import { CardinalPoint, ISLANDS_AIRPORTS_POLYGONS, Island, getAirportPlaceIdByCoordinates, isPointInPolygon } from './boundaries'
import { type AuthModel } from 'pocketbase'

type ActionStatusKeys = keyof typeof ACTION_STATUS
type ActionStatusValues = (typeof ACTION_STATUS)[ActionStatusKeys]
type ActionStatusInput = { success: ActionStatusValues; message?: string }

export function serializeNonPOJOs<T>(obj: T) {
  return structuredClone(obj)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function forceIndexSignature<T extends Record<string, any>>(constant: T, key: string) {
  if (key in constant) {
    return constant[key as keyof T]
  }
  return null
}

export function getIsland(value: string | number) {
  if (typeof value === 'number') {
    return ISLANDS.find((island) => island.id === value)
  }

  if (typeof value === 'string') {
    if (value.length === 2) {
      return ISLANDS.find((island) => island.short === value)
    } else {
      return ISLANDS.find((island) => island.long === value)
    }
  }
}

export function isMobileOrTablet(request: Request) {
  const userAgent = request.headers.get('user-agent') || ''
  const secUaMobile = request.headers.get('sec-ch-ua-mobile') || ''

  return secUaMobile === '?1' || IS_MOBILE_REGEX.some((toMatchItem) => userAgent.match(toMatchItem))
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getFormData(object: any) {
  const formData = new FormData()
  Object.keys(object).forEach((key) => formData.append(key, object[key]))
  return formData
}

export function getObject(formData: FormData) {
  return Object.fromEntries(formData.entries())
}
export function scrollIntoView(elementId: string): void {
  const el = document.querySelector(elementId) as HTMLElement

  if (!el) return

  const offset = el.getBoundingClientRect().top + window.scrollY
  const duration = 1000
  const startTime = performance.now()
  const endTime = startTime + duration

  function easeInOut(t: number): number {
    return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t
  }
  function animateScroll(currentTime: number): void {
    const elapsedTime = currentTime - startTime
    const scrollPosition = easeInOut(elapsedTime / duration) * offset

    window.scrollTo(0, scrollPosition)

    if (currentTime < endTime) {
      requestAnimationFrame(animateScroll)
    } else {
      window.scrollTo(0, offset)
    }
  }
  requestAnimationFrame(animateScroll)
}

export function getIntlFromLocale(locale: string) {
  switch (locale) {
    case 'es':
      return 'es-ES'
    case 'en':
      return 'en-GB'
    default:
      return 'es-ES'
  }
}

export function formatDecimal(locale: string, num: number) {
  const { format } = new Intl.NumberFormat(getIntlFromLocale(locale), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })

  return format(num)
}
function getCoordinates(place: CustomPlace | undefined): [number, number] | undefined {
  if (place && place?.coordinates?.lat !== undefined && place.coordinates.lng !== undefined) {
    const { lat, lng } = place.coordinates
    return [lat, lng]
  }
  return undefined
}
export function getInOutFromGooglePlaces(startPlace: CustomPlace | undefined, endPlace: CustomPlace | undefined) {
  const startCoordinates = getCoordinates(startPlace)
  const endCoordinates = getCoordinates(endPlace)
  for (const island of Object.values(Island)) {
    const airportPolygons = ISLANDS_AIRPORTS_POLYGONS[island]
    if (airportPolygons) {
      for (const cardinalPoint of Object.values(CardinalPoint)) {
        const airportPolygon = airportPolygons[cardinalPoint]
        if (airportPolygon) {
          if (startCoordinates && isPointInPolygon(startCoordinates, airportPolygon)) {
            return IN_OUT.IN
          }
          if (endCoordinates && isPointInPolygon(endCoordinates, airportPolygon)) {
            return IN_OUT.OUT
          }
        }
      }
    }
  }

  return IN_OUT.OTHER
}

export function formatCurrency(locale: string, num: number) {
  const { format } = new Intl.NumberFormat(getIntlFromLocale(locale), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    style: 'currency',
    currency: 'EUR'
  })

  return format(num)
}

export function formatDateTime(locale: string, date: string, options = { showTime: false }) {
  const aux = new Date(date)

  let formatOptions: { [key: string]: string } = {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }

  if (options.showTime) {
    formatOptions = {
      ...formatOptions,
      hour: '2-digit',
      minute: '2-digit'
    }
  }

  return new Intl.DateTimeFormat(locale, formatOptions).format(aux)
}

export function getBookingInfoToSave(formData: FormData): NinjaSaveBooking {
  const data = getObject(formData)
  const bookingInfo: NinjaSaveBooking = {
    FlightNumber: 'tmp',
    VehicleType: 'Bus',
    ServiceType: data['at_disposal'] ? 'AtDisposal' : 'Private',
    PaxNames: [''],
    ClientID: 1944,
    ExternalBookingID: 'tmp',
    ExternalServiceID: 'tmp',
    FlightDatetime: data['departure_iso_date'].toString(),
    PickupDatetime: data['arrival_iso_date'] ? data['arrival_iso_date'].toString() : data['departure_iso_date'].toString(),
    Email: data['email'].toString() || 'tmp@tmp.tmp',
    PaxAdults: Number(data['pax_adults'].toString()),
    PaxChildren: Number(data['pax_children'].toString()),
    PaxInfants: Number(data['pax_infants'].toString()),
    Special: 0,
    Golf: 0,
    BabySeat: 0,
    Bike: 0,
    Surf: 0,
    InOut: data['in_out'].toString(),
    StartPlacePosition: {
      Latitude: data['start_latitude'].toString(),
      Longitude: data['start_longitude'].toString(),
      Text: data['start_place'].toString()
    },
    Comments: '',
    EndTime: data['at_disposal'] ? data['departure_iso_date'].toString() : '',
    BillRequired: false
  }

  if (data['end_time']) {
    bookingInfo.EndTime = new Date(data['end_date'].toString() + ' ' + data['end_time'].toString()).toISOString()
  } else {
    bookingInfo.EndTime = undefined
  }
  if (data['end_place']) {
    bookingInfo.EndPlacePosition = {
      Latitude: data['end_latitude'].toString(),
      Longitude: data['end_longitude'].toString(),
      Text: data['end_place'].toString()
    }
  }

  if (bookingInfo.InOut === IN_OUT.IN) {
    const startAirport = getAirportPlaceIdByCoordinates([
      Number(bookingInfo.StartPlacePosition.Latitude),
      Number(bookingInfo.StartPlacePosition.Longitude)
    ])

    if (startAirport) {
      bookingInfo.StartPlaceID = startAirport
    }
  }

  if (bookingInfo.InOut === IN_OUT.OUT && bookingInfo.EndPlacePosition) {
    const endAirport = getAirportPlaceIdByCoordinates([
      Number(bookingInfo.EndPlacePosition.Latitude),
      Number(bookingInfo.EndPlacePosition.Longitude)
    ])

    if (endAirport) {
      bookingInfo.EndPlaceID = endAirport
    }
  }
  return bookingInfo
}

export function getPricesApiRequestBody(
  uid: string,
  booking: NinjaSaveBooking,
  combo: NinjaAvailabilityCombo,
  isRoundTrip = false
): PricesAPIRequest {
  return {
    uid,
    roundTrip: isRoundTrip,
    originCoordinates: [Number(booking.StartPlacePosition.Latitude), Number(booking.StartPlacePosition.Longitude)],
    destinationCoordinates: [
      Number(booking.EndPlacePosition ? booking.EndPlacePosition?.Latitude : booking.StartPlacePosition.Latitude),
      Number(booking.EndPlacePosition ? booking.EndPlacePosition?.Longitude : booking.StartPlacePosition.Longitude)
    ],
    departureDateTime: booking.FlightDatetime,
    arrivalDateTime: isRoundTrip ? booking.PickupDatetime : booking.EndTime ? booking.EndTime : '',
    // TODO: Update when passengers are distinguished by adults, children, and infants in bunpi (prices API).
    pax: booking.PaxAdults + booking.PaxChildren,
    serviceType: forceIndexSignature(PRICES_API_SERVICE_TYPES, combo.ServiceType) || 'PRIVADO',
    vehicleType: forceIndexSignature(PRICES_API_VEHICLES, combo.VehicleType) || 'BUS',
    kidStroller: 0,
    surfBoard: 0,
    golfBag: 0,
    bike: 0,
    specialLuggage: 0,
    extraLuggage: 0
  }
}

export function getCountryByCode(code: keyof typeof COUNTRIES): string {
  return COUNTRIES[code]
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function group(arr1: any[], arr2: any[], key: string) {
  return [...[...arr1, ...arr2].reduce((acc, curr) => acc.set(curr[key], { ...acc.get(curr[key]), ...curr }), new Map()).values()]
}

export function convertIsland(input: (typeof PRICES_API_ISLANDS)[keyof typeof PRICES_API_ISLANDS]): SelectObject | undefined {
  // TODO: Check if this is the best way...
  if (['TF', 'TFN', 'TNF'].includes(input)) {
    input = 'TF'
  }
  const island = ISLANDS.find((island) => island.short === input)
  if (island) {
    return { value: island.id, label: island.long }
  }
  return undefined
}

export function convertServiceType(input: string): SelectObject | undefined {
  const type = SERVICE_TYPES.find((type) => type.name.toLowerCase() === input.toLowerCase())
  if (type) {
    return { value: type.id, label: t.get(`common.${type.name}`) }
  }
  return undefined
}

export function convertCountry(input: string | undefined): SelectObject | undefined {
  if (isUndefined(input)) {
    return { value: 'undefined', label: t.get('common.country') }
  }

  const value = COUNTRIES[input as keyof typeof COUNTRIES]

  if (value) {
    return { value: input as string, label: value }
  }
}

export function capitalize(str: string) {
  str = str.toLowerCase()
  return str.charAt(0).toUpperCase() + str.slice(1)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isCorrectPaymentMethod = (method: any): method is (typeof PAYMENT_METHODS)[number] => PAYMENT_METHODS.includes(method)

export function redirectToActionStatus({ success, message }: ActionStatusInput) {
  let path = `/action?success=${success}`

  if (message) {
    path += `&message=${message}`
  }

  throw redirect(302, path)
}

export const sendPaymentRequest = async ({
  paymentData,
  NINJA_BASE_URL,
  NINJA_TOKEN
}: {
  paymentData: PaymentParams
  NINJA_BASE_URL: string
  NINJA_TOKEN: string
}) => {
  const response = await ninjaFetch(
    '/tpv/save_transaction',
    {
      baseUrl: NINJA_BASE_URL,
      token: NINJA_TOKEN
    },
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(paymentData)
    }
  )

  const result = await response.json()

  if (!result.Success) {
    console.error(result)
    return { success: false }
  }

  return { success: true, tpv: result.Message }
}

export const generatePassword = () => {
  const charset = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*()_+~`|}{[]:;?><,./-='
  let password = ''

  for (let i = 0, n = charset.length; i < 11; ++i) {
    password += charset.charAt(Math.floor(Math.random() * n))
  }
  return password
}

export const createBookerClient = async ({
  clientData,
  NINJA_BASE_URL,
  NINJA_TOKEN
}: {
  clientData: ClientData
  NINJA_BASE_URL: string
  NINJA_TOKEN: string
}) => {
  console.info('Creating booker client with data', clientData)
  const response = await ninjaFetch(
    '/sapiens/client/save_new_data',
    {
      baseUrl: NINJA_BASE_URL,
      token: NINJA_TOKEN
    },
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(clientData)
    }
  )
  if (response.status !== 200) {
    console.info('Error creating booker client')
    return { successClient: false }
  }
  const result = await response.json()
  if (result.Id) {
    return { successClient: true, bookerId: result.ClientId, relationId: result.Id }
  }

  return { successClient: false }
}
export const saveBookingBillingRelation = async ({
  data,
  NINJA_BASE_URL,
  NINJA_TOKEN
}: {
  data: { BookingId: number; RelationId: number }
  NINJA_BASE_URL: string
  NINJA_TOKEN: string
}) => {
  console.info('Creating booking billing relation with data', data)
  const response = await ninjaFetch(
    '/sapiens/booking/save_booking_billing_info',
    {
      baseUrl: NINJA_BASE_URL,
      token: NINJA_TOKEN
    },
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(data)
    }
  )

  if (response.status !== 200) {
    console.info('Error creating booking billing relation')
    return { successClient: false }
  }
}
export const getUserClientRelations = async ({
  bookerId,
  NINJA_BASE_URL,
  NINJA_TOKEN
}: {
  bookerId: number
  NINJA_BASE_URL: string
  NINJA_TOKEN: string
}) => {
  const response = await ninjaFetch(
    '/sapiens/client/get_relations/' + bookerId,
    {
      baseUrl: NINJA_BASE_URL,
      token: NINJA_TOKEN
    },
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )

  if (response.status !== 200) {
    console.info('Error creating booker client')
    return { successClient: false }
  }
  const result = await response.json()

  return result
}

export const saveBookerBooking = async ({
  booking,
  NINJA_BASE_URL,
  NINJA_TOKEN
}: {
  booking: NinjaSaveBooking
  NINJA_BASE_URL: string
  NINJA_TOKEN: string
}): Promise<{ success: boolean; messages?: string[]; bookerId?: number }> => {
  try {
    return await ninjaFetch(
      '/sapiens/booking/save',
      {
        baseUrl: NINJA_BASE_URL,
        token: NINJA_TOKEN
      },
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jsonToUtf(booking))
      }
    )
      .then(async (res) => {
        const response = await res.json()

        if (response.errors != undefined) {
          return { success: false, messages: response.errors }
        }

        return { success: true, bookerId: response.Id }
      })
      .catch((_) => {
        return { success: false, message: ['connection_error'] }
      })
  } catch (error) {
    console.log(error)
    console.error('Error saving booker booking', error)
    return { success: false }
  }
}

export const getPBServiceType = (serviceTypeName: string): string => {
  switch (serviceTypeName.toUpperCase()) {
    case 'PRIVATE':
      return String(env.PUBLIC_SERVICE_TYPE_PRIVATE_ID)
    case 'SHUTTLE':
      return String(env.PUBLIC_SERVICE_TYPE_SHUTTLE_ID)
    case 'DISCRETIONARY':
      return String(env.PUBLIC_SERVICE_TYPE_DISCRETIONARY_ID)
    case 'WHEELCHAIR':
      return String(env.PUBLIC_SERVICE_TYPE_WHEELCHAIR_ID)
    case 'EXCURSION':
      return String(env.PUBLIC_SERVICE_TYPE_EXCURSION_ID)
    case 'AT_DISPOSAL':
      return String(env.PUBLIC_SERVICE_TYPE_AT_DISPOSAL_ID)
    case 'PERMANENT':
      return String(env.PUBLIC_SERVICE_TYPE_PERMANENT_ID)
    default:
      return ''
  }
}
export const formatDateFromISOString = (isoStr: string | number | Date | undefined) => {
  if (!isoStr) return null
  const date = new Date(isoStr)
  const year = date.getFullYear().toString()
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const day = date.getDate().toString().padStart(2, '0')
  const hours = date.getHours().toString().padStart(2, '0')
  const minutes = date.getMinutes().toString().padStart(2, '0')
  return `${year}-${month}-${day} ${hours}:${minutes}`
}
export const parseRequestEstimateData = (data: FormData) => {

  const prefix = data.get('prefix')?.toString() ?? ''
  const phone = data.get('phone')?.toString() ?? ''
  const phoneAndPrefix = prefix && phone ? prefix + phone : phone

  const requestEstimate = {
    Name: data.get('name') ? data.get('name')?.toString() : 'Web_User',
    LastName: data.get('last_name') ? data.get('last_name')?.toString() : 'Web_User',
    Phone: phoneAndPrefix,
    Email: data.get('email')?.toString(),
    Island: data.get('island')?.toString(),
    ServiceType: data.get('service_type')?.toString(),
    Passengers: data.get('passengers')?.toString(),
    DisabledPassengers: data.get('disabled_passengers')?.toString(),
    StartPlace: data.get('start_place')?.toString(),
    DepartureDateTime: formatDateFromISOString(data.get('departure_datetime')?.toString()),
    EndPlace: data.get('end_place')?.toString(),
    ArrivalDateTime: formatDateFromISOString(data.get('arrival_datetime')?.toString()),
    Comments: data.get('comments')?.toString()
  }

  return requestEstimate
}

export const parseLostAndFoundsData = (data: FormData) => {

  const prefix = data.get('prefix')?.toString() ?? ''
  const phone = data.get('phone')?.toString() ?? ''
  const phoneAndPrefix = prefix && phone ? prefix + phone : phone

  const lostAndFoundsData = {
    ServiceType: Number(data.get('serviceType')?.toString()),
    Island: Number(data.get('island')?.toString()),
    DeparturePoint: data.get('departurePoint')?.toString(),
    Destination: data.get('destination')?.toString(),
    DepartureDate: data.get('departureDate')?.toString(),
    DepartureTime: data.get('departureTime')?.toString(),
    Category: Number(data.get('category')?.toString()),
    Subcategory: isNaN(Number(data.get('subcategory')?.toString())) ? undefined : Number(data.get('subcategory')?.toString()),
    Colour: data.get('colour')?.toString(),
    Description: data.get('description')?.toString(),
    Picture: data.get('picture')?.toString(),
    Name: data.get('name')?.toString(),
    Surname: data.get('surname')?.toString(),
    Email: data.get('email')?.toString(),
    Phone: phoneAndPrefix,
    LegalTerms: data.get('legalTerms')?.toString() == 'true',
    PrivacyTerms: data.get('privacyTerms')?.toString() == 'true'
  }

  return lostAndFoundsData
}

export function isAdapted(vehicleType: string) {
  return vehicleType.toLowerCase().includes('adapted') || vehicleType.toLowerCase().includes('wheelchair')
}

export function isLuxury(vehicleType: string) {
  return vehicleType.toLowerCase().includes('luxury')
}
export function isDisposal(serviceString: string) {
  return (
    serviceString.toLowerCase().includes('at_disposal') ||
    serviceString.toLowerCase().includes('atdisposal') ||
    serviceString.toLowerCase().includes('a_disposicion')
  )
}

export const randomExternalID = (type: 'booking' | 'service') => {
  return `WEB-${type.toUpperCase()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`
}

export const formatDate = (date: string, locale: string, time: boolean = true) => {
  const dated = new Date(date)
  const lng = locale == 'es' ? 'es-ES' : 'en-GB'

  const format = {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  } as Intl.DateTimeFormatOptions

  if (time) {
    format.minute = 'numeric'
    format.hour = 'numeric'
    format.hour12 = false
  }

  return dated.toLocaleDateString(lng, format)
}

export function getDateFromPurchaseInfo(date: string, dateType: 'departure' | 'arrival', purchaseInfo: PurchaseInfo): string {
  const isDeparture = dateType === 'departure'
  const isArrival = dateType === 'arrival'
  const dateHasHours = date && date.endsWith('00:00')

  if (
    date &&
    ((isDeparture && (purchaseInfo.isRoundTrip || purchaseInfo.inOut === IN_OUT.OUT)) ||
      (isArrival && (purchaseInfo.isRoundTrip || purchaseInfo.inOut === IN_OUT.IN)) ||
      purchaseInfo.inOut === IN_OUT.OTHER)
  ) {
    return dateHasHours ? date : date + ' 00:00'
  }

  return ''
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isPlacePositionType = (input: any): input is PlacePosition => input.Latitude !== undefined && input.Longitude !== undefined

export function getLangIdFromI18nLang(lang: string): number {
  switch (lang) {
    case 'es':
      return 1
    case 'en':
      return 6
    case 'ca':
    case 'val':
      return 2
    case 'eu':
      return 3
    case 'gl':
      return 4
    case 'fr':
      return 7
    case 'de':
      return 8
    case 'pt':
      return 9
    case 'it':
      return 10
    case 'ru':
      return 14
    case 'no':
      return 15
    default:
      return 6
  }
}
export const getUnAvailableServices = (availableServices: AvailableService[], isDisposal: boolean) => {
  const availablesServicesTypes = availableServices.map((as) => as.serviceType)
  const servicesTypes = Object.values(PRICES_API_SERVICE_TYPES)

  const unavailableServicesTypes = [
    ...new Set(servicesTypes.filter((av) => !availablesServicesTypes.includes(av as unknown as (typeof servicesTypes)[number])))
  ]

  if (!isDisposal) {
    unavailableServicesTypes.splice(unavailableServicesTypes.indexOf('A_DISPOSICION' as unknown as (typeof servicesTypes)[number]), 1)
  }

  let final = unavailableServicesTypes.map((un) => SERVICE_TYPES_NAMES[un as keyof typeof SERVICE_TYPES_NAMES] as unknown as string)
  if (isDisposal) {
    final = final.filter((f: string) => {
      return f == 'A_DISPOSICION'
    })
  }

  return final
}

export function getServiceTypeTexts(serviceType: string, vehicleType: string) {
  switch (vehicleType) {
    case PRICES_TO_NINJA_VEHICLE_TYPES.WheelChair:
      return {
        description: t.get('serviceTypeText.wheelchair_description'),
        sections: [
          {
            title: t.get('serviceTypeText.wheelchair_title1'),
            advantages: [
              t.get('serviceTypeText.wheelchair_advantage1'),
              t.get('serviceTypeText.wheelchair_advantage2'),
              t.get('serviceTypeText.wheelchair_advantage3')
            ]
          }
        ]
      }
    case PRICES_TO_NINJA_VEHICLE_TYPES.LuxuryCar:
    case PRICES_TO_NINJA_VEHICLE_TYPES.LUXURY_MINIVAN:
      return {
        description: t.get('serviceTypeText.premier_description'),
        sections: [
          {
            title: t.get('serviceTypeText.premier_title1'),
            advantages: [
              t.get('serviceTypeText.premier_advantage1'),
              t.get('serviceTypeText.premier_advantage2'),
              t.get('serviceTypeText.premier_advantage3')
            ]
          }
        ]
      }
    default:
      switch (serviceType) {
        case SERVICE_TYPES_NAMES.SHUTTLE:
          return {
            description: t.get('serviceTypeText.shuttle_description'),
            sections: [
              {
                title: t.get('serviceTypeText.shuttle_title1'),
                advantages: [
                  t.get('serviceTypeText.shuttle_advantages1'),
                  t.get('serviceTypeText.shuttle_advantages2'),
                  t.get('serviceTypeText.shuttle_advantages3')
                ]
              }
            ]
          }
        case SERVICE_TYPES_NAMES.PRIVATE:
          return {
            description: t.get('serviceTypeText.private_description'),
            sections: [
              {
                title: t.get('serviceTypeText.private_title1'),
                advantages: [
                  t.get('serviceTypeText.private_advantage1'),
                  t.get('serviceTypeText.private_advantage2'),
                  t.get('serviceTypeText.private_advantage3')
                ]
              }
            ]
          }
        case SERVICE_TYPES_NAMES.AT_DISPOSAL:
          return {
            description: t.get('serviceTypeText.atdisposal_description'),
            sections: [
              {
                title: t.get('serviceTypeText.atdisposal_title1'),
                advantages: [
                  t.get('serviceTypeText.atdisposal_advantage1'),
                  t.get('serviceTypeText.atdisposal_advantage2'),
                  t.get('serviceTypeText.atdisposal_advantage3')
                ]
              }
            ]
          }
        default:
          break
      }
  }
}
export const getServiceTypeByVehicle = (serviceType: string, vehicleType: string) => {
  switch (vehicleType) {
    case PRICES_TO_NINJA_VEHICLE_TYPES.WheelChair:
      return t.get('common.adapted_service')
    case PRICES_TO_NINJA_VEHICLE_TYPES.LuxuryCar:
    case PRICES_TO_NINJA_VEHICLE_TYPES.LUXURY_MINIVAN:
      return t.get('common.luxury_service')
    default:
      switch (serviceType) {
        case SERVICE_TYPES_NAMES.SHUTTLE:
          return t.get('common.shuttle')
        case SERVICE_TYPES_NAMES.PRIVATE:
          return t.get('common.private')
        case SERVICE_TYPES_NAMES.AT_DISPOSAL:
          return t.get('common.at_disposal')
        default:
          break
      }
  }
}

export const asciiToUtf = (string: string) => {
  const buffer = Buffer.from(string, 'ascii')
  return buffer.toString('utf8')
}

export function jsonToUtf<T>(obj: T) {
  const buffer = Buffer.from(JSON.stringify(obj), 'ascii')
  return JSON.parse(buffer.toString('utf8')) as T
}

export const getBookingDate = (formData: AnyObject, isReturn: boolean = false): string => {
  const inOut = formData.inOut?.toString() || ''
  if (isReturn) {
    if (inOut === 'In') return formData.departureDate?.toString() || ''
    if (inOut === 'Out') return formData.arrivalDate?.toString() || ''
    if (inOut === 'Other') return formData.pickupStartDateReturn?.toString() || ''
  }

  if (inOut === 'In') return formData.arrivalDate?.toString() || ''
  if (inOut === 'Out') return formData.departureDate?.toString() || ''
  if (inOut === 'Other') return formData.pickupStartDate?.toString() || ''

  return ''
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const isUndefined = (obj: any) => {
  return obj === undefined || obj == 'undefined'
}
export const getExcursionMode = (formData: AnyObject): string => {
  const pickUp: Date = new Date(formData.flightDatetime)
  const endTime: Date = new Date(formData.endTime)

  const differenceMs: number = endTime.getTime() - pickUp.getTime()

  const differenceHours: number = differenceMs / (1000 * 60 * 60)

  return differenceHours <= 4 ? EXCURSION_MODE.HALF_DAY : EXCURSION_MODE.FULL_DAY
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function debounce<T extends (...args: any[]) => any>(func: T, delay: number) {
  let timeoutId: ReturnType<typeof setTimeout>

  return function (this: ThisParameterType<T>, ...args: Parameters<T>) {
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const context = this

    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => {
      func.apply(context, args)
    }, delay)
  }
}

export function formatNoHourDate(date: Date): Date {
  return new Date(date.toISOString().split('T')[0])
}

export function getProvinceByPostalCode(postalCode: number): string | null {
  const firstTwoDigits = postalCode.toString().slice(0, 2)
  const provinces = CP_PROVINCES as { [key: string]: string }

  if (Object.hasOwn(provinces, firstTwoDigits)) {
    return provinces[firstTwoDigits]
  }

  return null
}
export function getIslandByCode(code: string): string | null {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const islands = ISLANDS_NUMERIC_KEY as any

  if (Object.hasOwn(ISLANDS_NUMERIC_KEY, code)) {
    return islands[code]
  }

  return null
}

export async function deleteBillingAddress(bookerId: string, NINJA_BASE_URL: string, NINJA_TOKEN: string) {
  const response = await ninjaFetch(
    '/sapiens/client/delete_relation/' + bookerId,
    {
      baseUrl: NINJA_BASE_URL,
      token: NINJA_TOKEN
    },
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
  if (response.status !== 200) {
    console.info('Error creating booker client')
    return { success: false }
  }
  return { success: true }
}
export function ValidateSpanishID(id: string): { valid: boolean } {
  const DNI_REGEX = /^(\d{8})([A-Z])$/
  const CIF_REGEX = /^([ABCDEFGHJKLMNPQRSUVW])(\d{7})([0-9A-J])$/
  const NIE_REGEX = /^[XYZ]\d{7}[A-Z]$/

  const validateDNI = (dni: string): boolean => {
    const dniLetters = 'TRWAGMYFPDXBNJZSQVHLCKE'
    const letter = dniLetters.charAt(parseInt(dni.substring(0, 8), 10) % 23)
    return letter === dni.charAt(8)
  }

  const validateNIE = (nie: string): boolean => {
    const niePrefixes: Record<string, string> = { X: '0', Y: '1', Z: '2' }
    const adjustedNie = niePrefixes[nie.charAt(0)] + nie.substring(1)
    return validateDNI(adjustedNie)
  }

  const validateCIF = (cif: string): boolean => {
    const match = cif.match(CIF_REGEX)
    if (!match) return false

    const letter = match[1]
    const number = match[2]
    const control = match[3]

    let evenSum = 0
    let oddSum = 0

    for (let i = 0; i < number.length; i++) {
      const n = parseInt(number.charAt(i), 10)
      if (i % 2 === 0) {
        oddSum += ((n * 2) % 10) + Math.floor((n * 2) / 10)
      } else {
        evenSum += n
      }
    }

    const controlDigit = (10 - ((evenSum + oddSum) % 10)) % 10
    const controlLetter = 'JABCDEFGHI'.charAt(controlDigit)

    if ('ABEH'.includes(letter)) {
      return control === String(controlDigit)
    } else if ('KPQS'.includes(letter)) {
      return control === controlLetter
    } else {
      return control === String(controlDigit) || control === controlLetter
    }
  }

  const idUpper = id.toUpperCase().replace(/\s/, '')
  if (DNI_REGEX.test(idUpper)) return { valid: validateDNI(idUpper) }
  if (CIF_REGEX.test(idUpper)) return { valid: validateCIF(idUpper) }
  if (NIE_REGEX.test(idUpper)) return { valid: validateNIE(idUpper) }
  return { valid: false }
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function gtagEvent(eventType: string, value: { [key: string]: any }) {
  if (window && 'dataLayer' in window) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-extra-semi
    ;(window.dataLayer as any).push({ event: eventType, ...value })
  }
}

export function calcDaysDiff(date1: Date, date2: Date) {
  const MS_PER_DAY = 1000 * 60 * 60 * 24

  const utc1 = Date.UTC(date1.getFullYear(), date1.getMonth(), date1.getDate())
  const utc2 = Date.UTC(date2.getFullYear(), date2.getMonth(), date2.getDate())

  return Math.floor((utc2 - utc1) / MS_PER_DAY)
}

export function getGtagEventData(step: number, source: PurchaseInfo, maybeReturnSource?: PurchaseInfo) {
  let actEvento: string | undefined = undefined
  let paso_proceso: string | undefined = undefined

  switch (step) {
    case 1:
      actEvento = '1. Seleccion tipo traslado'
      paso_proceso = '1. Seleccion tipo traslado'
      break
    case 2:
      actEvento = '2. Datos personales'
      paso_proceso = '2. Datos personales'
      break
    case 3:
      actEvento = '3. Resumen precompra'
      paso_proceso = '3. Resumen precompra'
      break
    case 4:
      actEvento = '4. Confirmacion compra'
      paso_proceso = '4. Confirmacion compra'
      break
    default:
      actEvento = 'Desconocido'
      paso_proceso = 'Desconocido'
      break
  }

  let userEmail: string | undefined = undefined
  if (source.hashedEmail) {
    userEmail = source.hashedEmail
  }

  const sourceDates: number[] = []
  if (source?.departureDate) {
    sourceDates.push(new Date(source.departureDate).valueOf())
  }

  if (source?.arrivalDate) {
    sourceDates.push(new Date(source.arrivalDate).valueOf())
  }

  if (source?.flightDatetime) {
    sourceDates.push(new Date(source.flightDatetime).valueOf())
  }

  if (maybeReturnSource?.departureDate) {
    sourceDates.push(new Date(maybeReturnSource.departureDate).valueOf())
  }

  if (maybeReturnSource?.arrivalDate) {
    sourceDates.push(new Date(maybeReturnSource.arrivalDate).valueOf())
  }

  if (maybeReturnSource?.arrivalDate) {
    sourceDates.push(new Date(maybeReturnSource.flightDatetime).valueOf())
  }

  const minSourceDate = Math.min(...sourceDates)
  const maxSourceDate = Math.max(...sourceDates)

  const departureDate = new Date(minSourceDate)
  const returnDate = new Date(maxSourceDate)

  let departureArrivalDiff = 0
  if (departureDate.valueOf() < returnDate.valueOf()) {
    departureArrivalDiff = calcDaysDiff(departureDate, returnDate)
  }

  const departureDateStr = formatDateTime('es-ES', departureDate.toISOString())
  let returnDateStr: string | undefined = formatDateTime('es-ES', returnDate.toISOString())

  if (!source.isRoundTrip) {
    returnDateStr = undefined
  }

  const beforeDays = calcDaysDiff(new Date(), departureDate)
  const beforeMinutes = beforeDays * 24 * 60

  return {
    catEvent: 'Proceso Reserva Traslado',
    actEvento,
    paso_proceso,
    userEmail,
    etiqEvento: source?.serviceType || 'Desconocido',
    fecha_ida: departureDateStr,
    fecha_vuelta: returnDateStr,
    tipo_producto: source?.serviceType || 'Desconocido',
    dias_antelacion: beforeDays,
    mins_antelacion: beforeMinutes,
    days_return: departureArrivalDiff > 0 ? departureArrivalDiff : undefined,
    n_pasajeros: source.paxAdults + source.paxChildren + source.paxInfants,
    n_adultos: source.paxAdults,
    n_ninos: source.paxChildren,
    n_bebes: source.paxInfants,
    ciudad: source.island
  }
}

export function cleanParamIslandName(param: string) {
  const words = param.split('-')
  let island = words[2]

  if (words[3]) {
    island += ' ' + words[3]
  }

  return island
}

export function getIslandFromUrlPath(path: string) {
  const islandNames = ISLANDS.map((i) => i.long)
  const urlPathSpaces = path.split('-').join(' ')

  for (const island of islandNames) {
    if (urlPathSpaces.includes(island.toLowerCase())) {
      return ISLANDS.find((i) => i.long === island)
    }
  }

  return undefined
}

export async function getSlideImages(): Promise<string[]> {
  const res = await fetch(`${env.PUBLIC_API_URL}/api/collections/home_slides/records?sort=order`)
  const jsonData = await res.json()

  if (res.ok) {
    let urls: string[] = []
    const baseSlidesURL = `${env.PUBLIC_API_URL}/api/files/`
    urls = await jsonData.items.map(
      (item: { collectionId: string; id: string; image: string }) => `${baseSlidesURL}${item.collectionId}/${item.id}/${item.image}`
    )
    return urls
  }
  throw new Error('Failed to fetch slide images')
}

export function buildPricesApiRequestAndMeetingPointsArray(
  combosData: NinjaAvailabilityCombo[],
  booking: NinjaSaveBooking,
  isRoundTrip: boolean
) {
  const reqBody: PricesAPIRequest[] = []
  const bookingsWithMeetingPoints: MeetingPointsAuxObj[] = []

  for (const combo of combosData) {
    const auxBooking = { ...booking }
    const uuid = crypto.randomUUID()

    if (combo.StartMeetingPoint || combo.EndMeetingPoint) {
      const ident: (typeof bookingsWithMeetingPoints)[number] = { uid: uuid, startMeetingPoint: false, endMeetingPoint: false }

      if (combo.StartMeetingPoint) {
        auxBooking.StartPlacePosition = combo.StartMeetingPoint
        ident.startMeetingPoint = combo.StartMeetingPoint
      }
      if (combo.EndMeetingPoint) {
        auxBooking.EndPlacePosition = combo.EndMeetingPoint
        ident.endMeetingPoint = combo.EndMeetingPoint
      }

      bookingsWithMeetingPoints.push(ident)
    }

    reqBody.push(getPricesApiRequestBody(uuid, auxBooking, combo, isRoundTrip))
  }

  return { reqBody, bookingsWithMeetingPoints }
}

export function getAvailableServicesByServiceType(
  reqBody: PricesAPIRequest[],
  pricesData: PricesAPIResponse[],
  bookingsWithMeetingPoints: MeetingPointsAuxObj[]
) {
  const availableServices = (group(reqBody, pricesData, 'uid') as AvailableService[]).filter((service) => service.price > 0)
  const minPriceItemByServiceType: { [key: string]: AvailableService } = {}

  availableServices.forEach((item) => {
    let { serviceType } = item
    const { vehicleType } = item
    const { uid } = item

    if (isLuxury(vehicleType || '')) {
      serviceType = `${serviceType}_LUXURY`
    }

    if (isAdapted(vehicleType || '')) {
      serviceType = `${serviceType}_ADAPTED`
    }

    item.hasMeetingPoint = bookingsWithMeetingPoints.some((b) => b.uid === uid)
    if (item.hasMeetingPoint) {
      const meetingPoint = bookingsWithMeetingPoints.find((b) => b.uid === uid)

      if (meetingPoint?.startMeetingPoint && isPlacePositionType(meetingPoint.startMeetingPoint)) {
        item.startMeetingPoint = meetingPoint.startMeetingPoint
      }
      if (meetingPoint?.endMeetingPoint && isPlacePositionType(meetingPoint.endMeetingPoint)) {
        item.endMeetingPoint = meetingPoint.endMeetingPoint
      }
    }

    const isPricesDiffGreaterThanOneCent = (item.metadata['priceWithFixedRate'] - item.price) > 0.1
    if (!item.metadata['isFixedRateUsed'] && item.price < item.metadata['priceWithFixedRate'] && isPricesDiffGreaterThanOneCent) {
      item.priceWithFixedRate = item.metadata['priceWithFixedRate']
    }

    if (!minPriceItemByServiceType[serviceType] || item.price < minPriceItemByServiceType[serviceType]?.price) {
      minPriceItemByServiceType[serviceType] = item
    }
  })

  return Object.values(minPriceItemByServiceType)
}

export function getDateAvailableServicesByServiceType(booking: NinjaSaveBooking, availableServicesByServiceType: AvailableService[]) {
  const currentDate = new Date()

  const minimunDateToBookShuttleOut = new Date()
  const minimunDateToBook = new Date()

  const bookingDateFull = new Date(booking.FlightDatetime)
  const bookingDate = new Date(bookingDateFull.getFullYear(), bookingDateFull.getMonth(), bookingDateFull.getDate())

  minimunDateToBookShuttleOut.setDate(currentDate.getDate() + MIN_DAYS_TO_BOOK_SHUTTLE_OUT)
  const minimunDateToBookShuttleOutSimple = new Date(
    minimunDateToBookShuttleOut.getFullYear(),
    minimunDateToBookShuttleOut.getMonth(),
    minimunDateToBookShuttleOut.getDate()
  )
  minimunDateToBook.setDate(currentDate.getDate() + MIN_DAYS_TO_BOOK)
  const minimunDateToBookSimple = new Date(minimunDateToBook.getFullYear(), minimunDateToBook.getMonth(), minimunDateToBook.getDate())

  return availableServicesByServiceType.filter((service) => {
    if (service.serviceType === SERVICE_TYPES_NAMES.SHUTTLE.toUpperCase()) {
      if (booking.InOut == IN_OUT.IN) {
        // This is due to the fixed 13:00 hours on the date query:
        const formattedBookingDate = formatNoHourDate(bookingDate)
        const formattedMinimumDateToBook = formatNoHourDate(minimunDateToBook)
        if (booking.StartPlaceID !== AIRPORTS_IDS.TFN || formattedBookingDate >= formattedMinimumDateToBook) {
          return service
        }
      }

      if (booking.InOut == IN_OUT.OUT && new Date(booking.FlightDatetime) >= minimunDateToBookShuttleOutSimple) {
        return service
      }
    } else {
      if (bookingDate >= minimunDateToBookSimple) {
        return service
      }
    }
  })
}

export function getUnAvailableServicesByServiceType(unAvailableServices: string[]) {
  const finalUnAvailableServices: { serviceType: string; vehicleType: string }[] = []

  unAvailableServices.forEach((ua) => {
    if (ua === SERVICE_TYPES_NAMES.PRIVATE) {
      finalUnAvailableServices.push({
        serviceType: SERVICE_TYPES_NAMES.PRIVATE,
        vehicleType: PRICES_TO_NINJA_VEHICLE_TYPES.CAR
      })
      finalUnAvailableServices.push({
        serviceType: SERVICE_TYPES_NAMES.PRIVATE,
        vehicleType: PRICES_TO_NINJA_VEHICLE_TYPES.LUXURY_CAR
      })
      finalUnAvailableServices.push({
        serviceType: SERVICE_TYPES_NAMES.PRIVATE,
        vehicleType: PRICES_TO_NINJA_VEHICLE_TYPES.ADAPTED_VEHICLE
      })
      return
    }
    finalUnAvailableServices.push({ serviceType: ua, vehicleType: PRICES_TO_NINJA_VEHICLE_TYPES.BUS })
  })

  return finalUnAvailableServices
}

export function getDefaultPurchaseInfoData(
  user: AuthModel | undefined,
  selectedInfo: HomeSelectedInfo,
  availableServices: AvailableService[]
) {
  const info: PurchaseInfo = {
    name: user?.name || '',
    email: user?.email || '',
    lastName: user?.last_name || '',
    phone: user?.phone || '',
    address: user?.address,
    nif: user?.nif,
    postalCode: user?.postal_code,
    country: user?.country,
    isRoundTrip: selectedInfo.isRoundTrip,
    startPlace: selectedInfo.booking.StartPlacePosition,
    endPlace: selectedInfo.booking.EndPlacePosition as PlacePosition,
    flightDatetime: selectedInfo.booking.FlightDatetime,
    paxAdults: selectedInfo.booking.PaxAdults,
    paxChildren: selectedInfo.booking.PaxChildren,
    paxInfants: selectedInfo.booking.PaxInfants,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    island: PRICES_API_ISLANDS[availableServices[0]?.metadata?.island as keyof typeof PRICES_API_ISLANDS],
    inOut: selectedInfo.booking.InOut,
    passengers: '',
    paxPmr: 0,
    price: 0,
    luggage: 0,
    observations: '',
    flightNumber: '',
    paymentMethod: 'card',
    insurancePrice: 0,
    special: 0,
    golf: 0,
    babySeats: 0,
    bike: 0,
    surf: 0,
    endTime: selectedInfo.booking.EndTime,
    billRequired: 0
  }

  info.departureDate = getDateFromPurchaseInfo(selectedInfo.departureDate, 'departure', info)
  info.arrivalDate = getDateFromPurchaseInfo(selectedInfo.arrivalDate, 'arrival', info)

  if (selectedInfo.booking.StartPlaceID) {
    info.startPlaceId = selectedInfo.booking.StartPlaceID
  }

  if (selectedInfo.booking.EndPlaceID) {
    info.endPlaceId = selectedInfo.booking.EndPlaceID
  }

  return info
}
